import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./DashboardHeader.css";

import {
  MdNotificationsNone,
  MdVerified,
  MdStorefront,
  MdRestaurant,
  MdBakeryDining,
  MdSoupKitchen,
  MdCelebration,
  MdLocalGroceryStore,
  MdChatBubbleOutline,
} from "react-icons/md";

import { getUnreadCount } from "../../../services/userNotificationService";

import API from "../../../../services/api";
import socket from "../../../../services/socket";

function DashboardHeader({
  provider,
  setDrawerOpen,
}) {

  const navigate = useNavigate();

  // =========================================================
  // Notification Unread Count
  // =========================================================

  const [unreadCount, setUnreadCount] = useState(0);

  // =========================================================
  // Message Unread Count
  // =========================================================

  const [unreadMessages, setUnreadMessages] = useState(0);


  // =========================================================
  // Load Notification Count
  // =========================================================

  useEffect(() => {

    fetchUnreadCount();

  }, []);


  const fetchUnreadCount = async () => {

    try {

      const res =
        await getUnreadCount();

      if (res.success) {

        setUnreadCount(
          res.unreadCount
        );

      }

    } catch (error) {

      console.error(
        "Notification Count Error:",
        error
      );

    }

  };


  // =========================================================
  // Load Existing Provider Unread Messages
  // =========================================================

  useEffect(() => {

    const loadUnreadMessages =
      async () => {

        try {

          const response =
            await API.get(
              "/conversations/provider"
            );

          if (
            response.data?.success
          ) {

            const conversations =
              response.data.conversations ||
              [];

            const totalUnread =
              conversations.reduce(
                (
                  total,
                  conversation
                ) => {

                  return (
                    total +
                    (
                      conversation.unreadProvider ||
                      0
                    )
                  );

                },
                0
              );

            setUnreadMessages(
              totalUnread
            );

          }

        } catch (error) {

          console.error(
            "Provider Unread Messages Error:",
            error
          );

        }

      };


    loadUnreadMessages();

  }, []);


  // =========================================================
  // Provider Socket Registration
  // =========================================================

  useEffect(() => {

    const providerId =
      provider?._id ||
      provider?.id;

    if (!providerId) {

      return;

    }


    const registerProvider =
      () => {

        console.log(
          "🍽 Dashboard Header - Provider Socket:",
          providerId
        );

        socket.emit(
          "register-provider",
          providerId
        );

      };


    if (socket.connected) {

      registerProvider();

    } else {

      socket.on(
        "connect",
        registerProvider
      );

    }


    return () => {

      socket.off(
        "connect",
        registerProvider
      );

    };

  }, [
    provider,
  ]);


  // =========================================================
  // Real-Time New Message
  // =========================================================

  useEffect(() => {

    const handleNewMessage =
      (message) => {

        console.log(
          "📩 Header received new message:",
          message
        );


        if (!message) {

          return;

        }


        // -----------------------------------------------------
        // IMPORTANT:
        // Provider should only get badge for CUSTOMER messages.
        //
        // Because the backend sends "new-message" to both
        // customer and provider rooms.
        // -----------------------------------------------------

        if (
          message.senderRole ===
          "customer"
        ) {

          setUnreadMessages(
            previous =>
              previous + 1
          );

        }

      };


    socket.on(
      "new-message",
      handleNewMessage
    );


    return () => {

      socket.off(
        "new-message",
        handleNewMessage
      );

    };

  }, []);


  // =========================================================
  // Open Provider Messages
  // =========================================================

  const handleOpenMessages =
    () => {

      // Clear header badge immediately
      setUnreadMessages(0);

      navigate(
        "/business/messages"
      );

    };


  // =========================================================
  // Greeting
  // =========================================================

  const hour =
    new Date().getHours();

  let greeting =
    "Good Evening";

  if (hour < 12) {

    greeting =
      "Good Morning";

  } else if (hour < 17) {

    greeting =
      "Good Afternoon";

  }


  // =========================================================
  // Today's Date
  // =========================================================

  const today =
    new Date().toLocaleDateString(
      "en-IN",
      {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    );


  // =========================================================
  // Provider Icon
  // =========================================================

  const getProviderIcon =
    () => {

      switch (
        provider?.providerType
      ) {

        case "Home Chef":

          return (
            <MdSoupKitchen />
          );


        case "Bakery":

          return (
            <MdBakeryDining />
          );


        case "Restaurant":

          return (
            <MdRestaurant />
          );


        case "Hotel":

          return (
            <MdRestaurant />
          );


        case "Catering":

          return (
            <MdCelebration />
          );


        case "Provision Store":

          return (
            <MdLocalGroceryStore />
          );


        default:

          return (
            <MdStorefront />
          );

      }

    };


  // =========================================================
  // JSX
  // =========================================================

  return (

    <div className="dashboard-header">

      {/* =====================================================
          TOP BAR
      ===================================================== */}

      <div className="header-top">


        {/* ===================================================
            BRAND
        =================================================== */}

        <div className="brand">

          <img
            src="/images/logo.png"
            alt="HomeFoods"
          />

          <div className="brand-text">

            <h2>
              HomeFoods
            </h2>

            <span>
              Business Portal
            </span>

          </div>

        </div>


        {/* ===================================================
            RIGHT ACTIONS
        =================================================== */}

        <div className="header-actions">


          {/* =================================================
              CHAT
          ================================================= */}

          <button
            type="button"
            className="chat-btn"
            onClick={
              handleOpenMessages
            }
            aria-label="Messages"
            title="Messages"
          >

            <MdChatBubbleOutline />

            {unreadMessages > 0 && (

              <span className="chat-count">

                {unreadMessages > 99
                  ? "99+"
                  : unreadMessages}

              </span>

            )}

          </button>


          {/* =================================================
              NOTIFICATIONS
          ================================================= */}

          <button
            type="button"
            className="notification-btn"
            onClick={() =>
              navigate(
                "/business/notifications"
              )
            }
            aria-label="Notifications"
            title="Notifications"
          >

            <MdNotificationsNone />

            {unreadCount > 0 && (

              <span className="notification-count">

                {unreadCount > 99
                  ? "99+"
                  : unreadCount}

              </span>

            )}

          </button>


          {/* =================================================
              MENU
          ================================================= */}

          <button
            type="button"
            className="menu-btn"
            onClick={() =>
              setDrawerOpen(true)
            }
            aria-label="Menu"
            title="Menu"
          >

            ☰

          </button>


        </div>

      </div>


      {/* =====================================================
          GREETING
      ===================================================== */}

      <div className="header-content">

        <h1>

          {greeting},{" "}

          <span>
            {provider?.ownerName ||
              "Provider"}
          </span>

          👋

        </h1>


        <p>

          Welcome back! Here's what's
          happening with your business
          today.

        </p>


        <small>

          {today}

        </small>

      </div>

    </div>

  );

}

export default DashboardHeader;